import { Download, X } from 'lucide-react'
import { Dialog as DialogPrimitive } from 'radix-ui'
import { useState, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

// The original photo, exactly as it was added, over everything else with a way back. It opens inside the app rather
// than at the file's own address because the app added to an iPhone's home screen has no back button: a photo
// opened there could only be left by closing the app. Without its own button, it opens from elsewhere (a menu).
export function OriginalPhoto({
  src,
  fallback,
  alt,
  children,
  open,
  onOpenChange,
}: {
  src: string
  // Shown in its place when this browser can't draw the original (an iPhone's HEIC file in Chrome, say).
  fallback: string
  alt: string
  children?: ReactNode
  open?: boolean
  onOpenChange?: (open: boolean) => void
}) {
  const [failed, setFailed] = useState(false)
  return (
    <DialogPrimitive.Root
      open={open}
      onOpenChange={(open) => {
        if (open) setFailed(false)
        onOpenChange?.(open)
      }}
    >
      {children && <DialogPrimitive.Trigger asChild>{children}</DialogPrimitive.Trigger>}
      <DialogPrimitive.Portal>
        <DialogPrimitive.Content
          aria-describedby={undefined}
          className="fixed inset-0 z-[60] flex flex-col bg-viewer text-viewer-foreground duration-200 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:animate-in data-[state=open]:fade-in-0"
        >
          <div className="flex items-center gap-2 px-3 pt-[calc(var(--safe-top)+0.75rem)] pb-2 sm:px-5">
            <DialogPrimitive.Close
              aria-label="Close the original"
              className={cn(round, 'hover:bg-viewer-foreground/12')}
            >
              <X aria-hidden />
            </DialogPrimitive.Close>
            <DialogPrimitive.Title className="min-w-0 flex-1 truncate text-center text-sm font-medium">
              The original
            </DialogPrimitive.Title>
            <a
              href={src}
              download
              aria-label="Download the original"
              className={cn(round, 'hover:bg-viewer-foreground/12')}
            >
              <Download className="size-5" aria-hidden />
            </a>
          </div>
          <div className="relative min-h-0 flex-1">
            <div className="absolute inset-0 flex items-center justify-center px-2 pb-2">
              <img
                src={failed ? fallback : src}
                alt={alt}
                onError={() => setFailed(true)}
                className="h-auto max-h-full w-auto max-w-full rounded-sm object-contain"
              />
            </div>
          </div>
          <p className="px-6 pb-[calc(var(--safe-bottom)+0.75rem)] text-center text-sm text-viewer-muted">
            {failed
              ? 'This browser can’t show the original, so this is a copy. Download keeps the original.'
              : 'Kept exactly as it was added. On a phone, press and hold it to save it.'}
          </p>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  )
}

const round =
  'grid size-11 shrink-0 place-items-center rounded-full outline-none transition-colors focus-visible:ring-[3px] focus-visible:ring-ring/60'
